import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import formatPrice from '../utils/formatPrice';
import { getAddresses } from '../services/addressService';
import { createGuestOrder, createOrder } from '../services/orderService';
import '../css/checkout.css';

const emptyGuest = {
  fullName: '', email: '', phone: '', addressLine1: '', addressLine2: '',
  city: '', state: '', pincode: ''
};

const CheckoutPage = () => {
  const { user } = useAuth();
  const { items, clearCart } = useCart();
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(Boolean(user));
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [guest, setGuest] = useState(emptyGuest);
  const [placing, setPlacing] = useState(false);
  const [step, setStep] = useState('address');
  const [paymentMethod, setPaymentMethod] = useState('COD');
  const itemsPrice = items.reduce((sum, item) => sum + (Number(item?.product?.price) || 0) * (Number(item?.qty) || 0), 0);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const list = await getAddresses();
        setAddresses(list || []);
        const selected = (list || []).find((address) => address.isDefault) || list?.[0];
        if (selected) setSelectedAddressId(selected._id);
      } catch (error) {
        console.error('Failed to load addresses', error);
      } finally {
        setLoadingAddresses(false);
      }
    };
    load();
  }, [user]);

  const updateGuest = (event) => setGuest((current) => ({ ...current, [event.target.name]: event.target.value }));

  const handleContinueToPayment = () => {
    if (user && !selectedAddressId) return alert('Please select a shipping address first');
    if (!user) {
      const required = ['fullName', 'phone', 'addressLine1', 'city', 'state', 'pincode'];
      if ((!guest.email.trim() && !guest.phone.trim()) || required.some((key) => !guest[key].trim())) {
        return alert('Please provide your contact and complete shipping details');
      }
    }
    setStep('payment');
  };

  const handlePlaceOrder = async () => {
    if (placing || items.length === 0) return;
    setPlacing(true);
    try {
      const payload = user
        ? { addressId: selectedAddressId, paymentMethod }
        : {
            customer: { email: guest.email, phone: guest.phone },
            shippingAddress: {
              fullName: guest.fullName, phone: guest.phone,
              addressLine1: guest.addressLine1, addressLine2: guest.addressLine2,
              city: guest.city, state: guest.state, pincode: guest.pincode
            },
            items: items.map((item) => ({
              productId: item.product._id, qty: item.qty, size: item.size, color: item.color
            })),
            paymentMethod
          };
      const order = user ? await createOrder(payload) : await createGuestOrder(payload);
      await clearCart();
      navigate(`/order-success/${encodeURIComponent(order.orderId || order._id)}`);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to place order');
    } finally {
      setPlacing(false);
    }
  };

  if (items.length === 0 && !placing) {
    return <div className="checkout-page"><h1 className="page-title">Checkout</h1><p className="checkout-empty">Your cart is empty.</p></div>;
  }

  return (
    <div className="checkout-page">
      <h1 className="page-title">Checkout</h1>
      <div className="checkout-layout">
        <div className="checkout-left">
          {step === 'address' && (
            <section className="checkout-addresses">
              <h2>{user ? 'Shipping Address' : 'Contact & Shipping Details'}</h2>
              {user ? (
                loadingAddresses ? <div>Loading addresses...</div> : addresses.length === 0 ? (
                  <div><p>No shipping addresses found.</p><button className="btn-add-address" onClick={() => navigate('/account', { state: { activeTab: 'address' } })}>Add Address +</button></div>
                ) : (
                  <div className="address-list">
                    {addresses.map((address) => (
                      <label key={address._id} className={`address-card ${address.isDefault ? 'default' : ''}`}>
                        <input type="radio" name="shippingAddress" checked={selectedAddressId === address._id} onChange={() => setSelectedAddressId(address._id)} />
                        <div className="address-body">
                          <div className="address-top"><strong>{address.fullName}</strong>{address.isDefault && <span className="address-default">Default</span>}</div>
                          <div className="address-line">{address.address}</div>
                          {address.locality && <div className="address-line">{address.locality}</div>}
                          <div className="address-meta">{address.city} - {address.pincode}, {address.state}</div>
                          <div className="address-meta">Phone: {address.phone}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                )
              ) : (
                <div className="guest-checkout-form">
                  <label>Full name<input name="fullName" value={guest.fullName} onChange={updateGuest} /></label>
                  <label>Email<input type="email" name="email" value={guest.email} onChange={updateGuest} placeholder="Email or phone is required" /></label>
                  <label>Phone<input type="tel" name="phone" value={guest.phone} onChange={updateGuest} /></label>
                  <label className="full-width">Address<input name="addressLine1" value={guest.addressLine1} onChange={updateGuest} /></label>
                  <label className="full-width">Apartment, landmark (optional)<input name="addressLine2" value={guest.addressLine2} onChange={updateGuest} /></label>
                  <label>City<input name="city" value={guest.city} onChange={updateGuest} /></label>
                  <label>State<input name="state" value={guest.state} onChange={updateGuest} /></label>
                  <label>Pincode<input name="pincode" value={guest.pincode} onChange={updateGuest} /></label>
                </div>
              )}
            </section>
          )}
          {step === 'payment' && (
            <section className="checkout-payment"><h2>Payment Method</h2><div className="payment-options">
              <label className="payment-option"><input type="radio" checked={paymentMethod === 'COD'} onChange={() => setPaymentMethod('COD')} /><div className="payment-content"><strong>Cash on Delivery</strong><p>Pay when your order is delivered</p></div></label>
              <label className="payment-option"><input type="radio" disabled /><div className="payment-content"><strong>UPI Payment</strong><p>This feature will be enabled soon</p></div></label>
            </div></section>
          )}
        </div>
        <aside className="checkout-right">
          <h2>Order Summary</h2>
          <div className="summary-row"><span>Items</span><span>{items.reduce((sum, item) => sum + Number(item.qty || 0), 0)}</span></div>
          <div className="summary-row"><span>Subtotal</span><span>{formatPrice(itemsPrice)}</span></div>
          <div className="summary-row total"><span>Grand Total</span><span>{formatPrice(itemsPrice)}</span></div>
          {step === 'address' ? <button className="btn-continue-payment" onClick={handleContinueToPayment}>Continue to Payment</button> : <>
            <button className="btn-back-to-address" onClick={() => setStep('address')}>Back to Details</button>
            <button className="btn-place-order wide" onClick={handlePlaceOrder} disabled={placing}>{placing ? 'Placing Order...' : 'Place Order (COD)'}</button>
          </>}
        </aside>
      </div>
    </div>
  );
};

export default CheckoutPage;
